/**
 * Small components for formatting text correctly in the GUI.
 */

import * as React from 'react';
import { cn } from '../components/utils/cn';
import { css, colors, fontSizes, fontWeights } from './stylesheet';

interface TypographyProps<T> {
	className?: string
	style?: React.HTMLAttributes<T>["style"]
	children?: React.ReactNode
	onClick?: any
}

export const Text = (props: TypographyProps<HTMLSpanElement>) => {
	return (
		<span className={cn(css.NormalText, props.className)} onClick={props.onClick} style={props.style}>{props.children}</span>
	);
}

export const Header = (props: TypographyProps<HTMLDivElement>) => {
	return (
		<div className={cn(css.HeaderText, props.className)} style={props.style}>{props.children}</div>
	);
}

export const Highlight = (props: TypographyProps<HTMLSpanElement>) => {
	return (
		<span className={cn(css.HighlightText, props.className)} style={props.style}>{props.children}</span>
	);
}

export const ViewHeadline = (props: TypographyProps<HTMLDivElement>) => {
	return (
		<div className={cn(css.ScrollableViewHeadline, props.className)} style={props.style}>{props.children}</div>
	);
}

export const Title = (props: TypographyProps<HTMLDivElement>) => {
	return (
		<div className={cn(css.TitleText, props.className)} style={props.style}>{props.children}</div>
	);
}

/** Standard Headliner with subtitle or underline */
export const Headliner = (props: {
	icon?: any,
	text: any,
	extra?: string
	right?: React.ReactNode
	notRequireDivider?: boolean
	notRequirePadding?: boolean
	style?: any
}) => {
	const styles: React.CSSProperties = {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		...props.style || {},
	};
	return props.notRequirePadding ?
	<>
	<div style={styles}>
		<Header style={{ color: colors.primary, fontWeight: fontWeights.medium, fontSize: fontSizes.xl, paddingTop: '10px', width: '100%' }}>{props.text}</Header>
		{props.right}
	</div>
	{props.extra
		? <Text>{props.extra}</Text>
		: props.notRequireDivider ? null : <SectionDivider />
	}
	</>
	:
	props.icon ?
	<>
	  <Inset>
		<div style={styles}>
			<div style={{ display: "flex", alignItems: "center" }}>
			{props.icon}
			<Inset>
				<Header style={{ color: colors.primary, fontWeight: fontWeights.medium, fontSize: fontSizes.xl }}>  {props.text}</Header>
			</Inset>
			</div>
			{props.right}
		</div>
		{props.extra
			? <Text>{props.extra}</Text>
			: props.notRequireDivider ? null : <SectionDivider style={{ marginTop: '6px' }} />
		}
	  </Inset>
	</>
	:
	<Inset>
		<div style={styles}>
			<Header style={{ color: colors.primary, fontWeight: fontWeights.medium, fontSize: fontSizes.xl }}>{props.text}</Header>
			{props.right}
		</div>
		{props.extra
			? <Text>{props.extra}</Text>
			: props.notRequireDivider ? null : <SectionDivider />
		}
	</Inset>
}

/** Standard Headliner with subtitle or underline */
export const ProductHeadliner = (props: {
	text: string,
	extra?: string
	right?: React.ReactNode
	notRequireDivider?: boolean
	notRequirePadding?: boolean
	style?: any
	searchfunc?: () => void
}) => {
	const styles: React.CSSProperties = {
		display: 'flex',
		justifyContent: 'space-between',
		...props.style || {},
	};
	return props.notRequirePadding ?
	<>
	<div style={styles}>
			<Header style={{ color: colors.primary, fontWeight: fontWeights.medium, fontSize: fontSizes.xl, paddingTop: '10px', width: '100%' }}>{props.text}</Header>
			{props.right}
	</div>
	{props.extra
		? <Text>{props.extra}</Text>
		: props.notRequireDivider ? null : <SectionDivider />
	}
	</>
	:
	<Inset>
		<div style={styles}>
			<Header style={{ color: colors.primary, fontWeight: fontWeights.medium, fontSize: fontSizes.xl }}>{props.text}</Header>
			{props.right}
		</div>
		{props.extra
			? <Text>{props.extra}</Text>
			: props.notRequireDivider ? null : <SectionDivider />
		}
	</Inset>
}

export const SubHeadliner = (props: {
	text: React.ReactNode,
	style?: object,
	className?: any
}) => {
	return (
		<h3 className={props.className} style={{ fontSize: fontSizes.sm, ...props.style }}>{props.text}</h3>
	);
}

// Import spacing components used by Headliner/ProductHeadliner
import { Inset, SectionDivider } from './spacing';
